import { Component, EventEmitter, Input, Output } from '@angular/core';
import { fade, openClose } from '@core/animations/animations';
import { capitalize } from '@core/util/string';
import { GenderOptions, GenderType, TitleOptions } from '@models/clients';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';
import { ICON } from '@core/util/icon';
import { Address } from '@models/address';
import { addressString } from '@core/util/address';
import {
	AttentionType,
	Branch,
	EncumbranceCorrespondence,
	EncumbranceCorrespondenceType,
	FinancialInstitutionLender,
	Mortgagee,
} from '@models/lender';
import { LawFirm } from '@models/lawFirms';

const AttentionTypeLabels = {
	[AttentionType.DEPARTMENT]: 'Department',
	[AttentionType.CONTACT]: 'Contact',
};

const CorrespondenceOptions: RadioGroupItem<EncumbranceCorrespondenceType>[] = [
	{ label: 'Mortgagee', value: EncumbranceCorrespondenceType.MORTGAGEE },
	{ label: 'Solicitor', value: EncumbranceCorrespondenceType.SOLICITOR },
];

const AttentionTypeOptions: DropdownOption<AttentionType>[] = [
	{
		name: AttentionTypeLabels[AttentionType.DEPARTMENT],
		value: AttentionType.DEPARTMENT,
	},
	{
		name: AttentionTypeLabels[AttentionType.CONTACT],
		value: AttentionType.CONTACT,
	},
];

@UntilDestroy()
@Component({
	selector: 'app-institution',
	templateUrl: './institution.component.html',
	animations: [openClose(), fade()],
	standalone: false,
})
export class EncumbranceInstitutionComponent {
	@Input() showDischargeFields: boolean;
	@Output() updateMortgagee: EventEmitter<Mortgagee> =
		new EventEmitter<Mortgagee>();
	@Input() set mortgagee(tmpMortgagee: Mortgagee) {
		if (!tmpMortgagee.correspondence) {
			tmpMortgagee.correspondence = {} as EncumbranceCorrespondence;
		}

		this._mortgagee = tmpMortgagee;
	}

	@Input() showCorrespondence = true;
	@Input() pushChangesToGlobalData: boolean;

	public _mortgagee: Mortgagee;
	public ICON = ICON;
	public titleOptions = TitleOptions;
	public correspondenceOptions = CorrespondenceOptions;
	public correspondenceTypes = EncumbranceCorrespondenceType;
	public genderOptions = GenderOptions;
	public attentionOptions = AttentionTypeOptions;
	public attentionTypes = AttentionType;
	public financialInstitutions = [];

	onSolicitorUpdate(value: LawFirm) {
		this._mortgagee.solicitor = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	editBranch(value: Branch) {
		this._mortgagee.financialInstitution.branch = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	removeBranch() {
		this._mortgagee.financialInstitution.branch = null;
		this.updateMortgagee.emit(this._mortgagee);
	}

	setBranch(value: Branch) {
		this._mortgagee.financialInstitution.branch = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	createBranch(value: Branch) {
		this._mortgagee.financialInstitution.branch = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	editInstitution(value: FinancialInstitutionLender) {
		this._mortgagee.financialInstitution = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	selectInstitution(value: FinancialInstitutionLender) {
		this._mortgagee.financialInstitution = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	createInstitution(value: FinancialInstitutionLender) {
		this._mortgagee.financialInstitution = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	removeInstitution() {
		this._mortgagee.financialInstitution = null;
		this.updateMortgagee.emit(this._mortgagee);
	}

	setAttentionType(value: DropdownResult<DropdownOption<AttentionType>>) {
		this._mortgagee.correspondence.attentionType = value.result.value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	setAttention(value: string) {
		this._mortgagee.correspondence.attention = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	setCorrespondenceType(value: EncumbranceCorrespondenceType) {
		this._mortgagee.correspondenceType = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	get attentionType() {
		const label =
			AttentionTypeLabels[this?._mortgagee?.correspondence?.attentionType];

		return capitalize(label ?? '');
	}

	setSameAsInstitution(value: boolean) {
		this._mortgagee.correspondence.sameAsInstitution = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	setPrefix(value: DropdownResult<{ name: string }>) {
		this._mortgagee.correspondence.prefix = value.result.name;
		this.updateMortgagee.emit(this._mortgagee);
	}

	setGender(value: GenderType) {
		this._mortgagee.correspondence.gender = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	setFirstName(value: string) {
		this._mortgagee.correspondence.firstName = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	setLastName(value: string) {
		this._mortgagee.correspondence.lastName = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	setWorkPhone(value: string) {
		this._mortgagee.correspondence.workPhone = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	setPhone(value: string) {
		this._mortgagee.correspondence.phone = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	setFax(value: string) {
		this._mortgagee.correspondence.fax = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	setEmail(value: string) {
		this._mortgagee.correspondence.email = value;
		this.updateMortgagee.emit(this._mortgagee);
	}

	setAddress(address: Address): string {
		return addressString(address);
	}
}
